'use client';

import { useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Button, Text, Spinner } from '@terra/ui-design-system';
import { useMainContentTabs, type Tab } from '../../store/layoutStore';
import {
  useProjectStore,
  useTargetId,
  useTargetType,
  type PortalTarget,
} from '../../store/projectStore';
import { useIsAiApplicationKindPending } from '../../hooks/useIsAiApplicationKindPending';
import Image from 'next/image';
import { RuntimeEnvironmentNotice } from '../runtime-environment/RuntimeEnvironmentNotice';
import { TargetSettingsTab } from '../settings/TargetSettingsTab';
import { ApplicationSelectorWithoutTitle } from '../settings/target-settings/ApplicationSelectorWithoutTitle';
import { useAutoSelectCompany } from '../../hooks/useAutoSelectCompany';
import { CertificateInstallSection } from '../CertificateInstallSection';
import { EndpointDetailsTab } from '../tabs/EndpointDetailsTab';
import { UnverifiedEndpointDetailsTab } from '../tabs/UnverifiedEndpointDetailsTab';
import { ApplicationOverviewTab } from '../tabs/ApplicationOverviewTab';
import { GlobalRequestsTab } from '../tabs/GlobalRequestsTab';
import { CodeModule } from '../CodeModule';
import { useAppTargetId } from '../../store/projectStore';
import { useSourceCodeInfo } from '../../hooks/dashboard/useSourceCodeInfo';
import { WebSocketHistoryTab } from '../tabs/WebSocketHistoryTab';
import { InterceptTabWrapper } from '../tabs/InterceptTabWrapper';
import { MatchReplaceTabWrapper } from '../tabs/MatchReplaceTabWrapper';
import { ReplayTabWrapper } from '../tabs/ReplayTabWrapper';
import { ProxySettingsTabWrapper } from '../tabs/ProxySettingsTabWrapper';
import { ComparerTabWrapper } from '../tabs/ComparerTabWrapper';
import { WebPageDetailsTab } from '../tabs/WebPageDetailsTab';
import { SignalDetailsTab } from '../tabs/SignalDetailsTab';
import { FindingDetailsTab } from '../tabs/FindingDetailsTab';
import { TraceDetailsTab } from '../tabs/TraceDetailsTab';
import { AgentExecutionTraceTab } from '../tabs/AgentExecutionTraceTab';
import { AgentExecutionsContent } from '../tabs/AgentExecutionsContent';
import { InteractTab } from '../tabs/InteractTab';
import { SignalsContent } from '../tabs/SignalsContent';
import { FindingsContent } from '../tabs/FindingsContent';
import { CoPilotExecutionTraceTab } from '../tabs/CoPilotExecutionTraceTab';
import { ExecutionsListContent } from '../tabs/ExecutionsListContent';
import { ReportsContent } from '../tabs/ReportsContent';
import { ReportsPageHeader } from '../tabs/reports-header/ReportsPageHeader';
import { AssetsTabWrapper } from '../tabs/AssetsTabWrapper';
import { AssetsPageHeader } from '../tabs/assets-header/AssetsPageHeader';
import { FindingsPageHeader } from '../tabs/findings-header/FindingsPageHeader';
import { SignalsPageHeader } from '../tabs/signals-header/SignalsPageHeader';
import { AiAssetsTabWrapper } from '../tabs/AiAssetsTabWrapper';
import { ExternalNetworkAssetsContent } from '../tabs/surface-assets/SurfaceAssetsContent';
import { SurfaceAssetDetailTab } from '../tabs/SurfaceAssetDetailTab';
import { AiComponentDetailTab } from '../tabs/AiComponentDetailTab';
import { SkillsContent } from '../tabs/SkillsContent';
import { SkillDetailContent } from '../tabs/SkillDetailContent';
import { SkillEditorContent } from '../tabs/SkillEditorContent';
import { TargetOverviewContent } from '../tabs/target-overview/TargetOverviewContent';
import { NetworkGraphContent } from '../tabs/target-overview/NetworkGraphContent';
import { HitlEditTab } from '../tabs/HitlEditTab';
import { HttpViewerTab } from '../tabs/HttpViewerTab';
import { NodeArgsViewerTab } from '../tabs/NodeArgsViewerTab';
import { BrowserStreamTab } from '../tabs/BrowserStreamTab';
import { PlanViewerTab } from '../tabs/PlanViewerTab';
import { PRODUCT_DISPLAY_NAME } from '@terra/shared-utils';
import { isAppTarget, TARGET_TYPE } from '../../utils/target-scope.utils';
import { WELCOME_TARGET_PICKER_DATA_ID } from '../chat/CoPilotNoTargetState';
import { useTranslations } from 'next-intl';

function CodeListTabPanel() {
  const appTargetId = useAppTargetId();
  const { data: sourceCodeInfo, isLoading } = useSourceCodeInfo(
    appTargetId ?? '',
  );

  if (!appTargetId) {
    return null;
  }

  if (!isLoading && sourceCodeInfo && !sourceCodeInfo.isConnected) {
    return null;
  }

  return <CodeModule />;
}

function WelcomeTab() {
  const t = useTranslations('proxy.mainContentArea');
  const tLayout = useTranslations('layout');
  const { companyId, selectedTarget } = useProjectStore();
  const targetId = useTargetId();
  const { isCompanyReady, companyResolveError, refetchCompanies } =
    useAutoSelectCompany();
  const { openTab } = useMainContentTabs();
  const [isWorkspaceErrorDismissed, setIsWorkspaceErrorDismissed] =
    useState(false);

  const hasProjectSelected = companyId && targetId;
  const hasWorkspaceError = !!companyResolveError && !isWorkspaceErrorDismissed;
  const handleApplicationSelected = (target: PortalTarget) => {
    if (hasProjectSelected) {
      return;
    }

    openTab({
      id: 'application-overview',
      title: isAppTarget(target.targetType)
        ? tLayout('home')
        : tLayout('targetOverview'),
      type: 'application',
    });
  };

  return (
    <VStack
      gap="6"
      alignItems="center"
      justifyContent="center"
      h="full"
      px="8"
      py="12"
    >
      <VStack gap="4" alignItems="center" w="full">
        <Image
          src="/images/icon-orange.svg"
          alt={'Brand Logo'}
          width={48}
          height={48}
        />
        <VStack gap="2" alignItems="center" w="full">
          <Text fontSize="xl" fontWeight="semibold" color="fg.default">
            Welcome to {PRODUCT_DISPLAY_NAME}
          </Text>
          <Text fontSize="md" color="fg.muted" textAlign="center" maxW="md">
            {hasWorkspaceError
              ? t('workspaceUnavailableDescription')
              : hasProjectSelected
                ? `Connected to ${selectedTarget?.name}`
                : t('selectApplicationToGetStarted')}
          </Text>
        </VStack>
      </VStack>

      <CertificateInstallSection />

      {/* Project Selection */}
      <VStack gap="4" w="full" maxW="md" alignItems="stretch">
        <VStack gap="3" alignItems="flex-start" w="full">
          <Text fontSize="lg" fontWeight="medium" color="fg.default">
            {hasWorkspaceError
              ? t('workspaceUnavailableTitle')
              : t('projectConfiguration')}
          </Text>
          {!hasWorkspaceError ? (
            <Text fontSize="sm" color="fg.muted">
              {t('chooseTargetDescription')}
            </Text>
          ) : null}
        </VStack>

        <VStack gap="4" w="full">
          <Box data-id={WELCOME_TARGET_PICKER_DATA_ID} w="full">
            <ApplicationSelectorWithoutTitle
              disabled={!isCompanyReady}
              size="sm"
              onApplicationSelected={handleApplicationSelected}
            />
          </Box>

          {hasWorkspaceError ? (
            <HStack gap="3" width="full" justify="flex-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsWorkspaceErrorDismissed(true)}
              >
                {t('continueAnyway')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsWorkspaceErrorDismissed(false);
                  void refetchCompanies();
                }}
              >
                {t('retryNow')}
              </Button>
            </HStack>
          ) : null}

          <RuntimeEnvironmentNotice />
        </VStack>
      </VStack>
    </VStack>
  );
}

function DomainTab({ tab }: { tab: Tab }) {
  return (
    <VStack gap="4" p="6" alignItems="flex-start" h="full">
      <VStack gap="2" alignItems="flex-start">
        <Text fontSize="lg" fontWeight="semibold" color="fg.default">
          {tab.title}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          Domain details and endpoints
        </Text>
      </VStack>

      <Box
        p="4"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        w="full"
        bg="bg.subtle"
      >
        <Text fontSize="sm" color="fg.muted">
          Domain content will be displayed here.
          {tab.data ? (
            <>
              <br />
              <Text as="span" fontFamily="mono" fontSize="xs">
                {JSON.stringify(tab.data, null, 2)}
              </Text>
            </>
          ) : null}
        </Text>
      </Box>
    </VStack>
  );
}

export function MainContentTabPanel({
  tab,
  activePageEntry,
}: {
  tab: Tab;
  activePageEntry: ReturnType<typeof useMainContentTabs>['activePageEntry'];
}) {
  const targetType = useTargetType();
  const isAiApplicationKindPending = useIsAiApplicationKindPending();

  switch (tab.type) {
    case 'welcome':
      return <WelcomeTab />;
    case 'application': {
      const pageIdForView = activePageEntry?.pageId ?? tab.id;

      if (
        pageIdForView.startsWith('application-overview') ||
        pageIdForView.startsWith('dashboard')
      ) {
        return <TargetOverviewContent />;
      }

      return <ApplicationOverviewTab tab={tab} />;
    }

    case 'network-graph':
      return <NetworkGraphContent />;
    case 'domain':
      return <DomainTab tab={tab} />;
    case 'endpoint':
      return <EndpointDetailsTab tab={tab} />;
    case 'unverified-endpoint':
      return <UnverifiedEndpointDetailsTab tab={tab} />;
    case 'webpage':
      return <WebPageDetailsTab tab={tab} />;
    case 'settings':
      return <TargetSettingsTab tabId={tab.id} />;
    case 'requests':
      return <GlobalRequestsTab tabId={tab.id} />;
    case 'websocket':
      return <WebSocketHistoryTab />;
    case 'intercept':
      return <InterceptTabWrapper />;
    case 'match-replace':
      return <MatchReplaceTabWrapper />;
    case 'replay':
      return <ReplayTabWrapper />;
    case 'proxy-settings':
      return <ProxySettingsTabWrapper tabId={tab.id} />;
    case 'comparer':
      return <ComparerTabWrapper />;
    case 'executions':
      return <TraceDetailsTab tab={tab} />;
    case 'agent-execution':
      return <AgentExecutionTraceTab tab={tab} />;
    case 'signal':
      return <SignalDetailsTab tab={tab} />;
    case 'finding':
      return <FindingDetailsTab tab={tab} />;
    case 'interact':
      return <InteractTab />;
    case 'signals-list':
      return (
        <Box h="full" w="full" overflow="hidden">
          <SignalsContent header={<SignalsPageHeader />} />
        </Box>
      );
    case 'findings-list':
      return (
        <Box h="full" w="full" overflow="hidden">
          <FindingsContent header={<FindingsPageHeader />} />
        </Box>
      );
    case 'reports-list':
      return (
        <Box h="full" w="full" overflow="hidden">
          <ReportsContent header={<ReportsPageHeader />} />
        </Box>
      );
    case 'executions-list':
      return (
        <Box h="full" w="full" overflow="hidden">
          <ExecutionsListContent />
        </Box>
      );
    case 'assets-list':
      return (
        <Box h="full" w="full" overflow="hidden">
          {isAiApplicationKindPending ? (
            <Box
              w="full"
              h="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner />
            </Box>
          ) : targetType === TARGET_TYPE.AI_APPLICATION ? (
            <AiAssetsTabWrapper />
          ) : isAppTarget(targetType) ? (
            <AssetsTabWrapper header={<AssetsPageHeader />} />
          ) : (
            <ExternalNetworkAssetsContent />
          )}
        </Box>
      );
    case 'surface-asset':
      return <SurfaceAssetDetailTab tab={tab} />;
    case 'ai-component':
      return <AiComponentDetailTab tab={tab} />;
    case 'code-list':
      return (
        <Box h="full" w="full" overflow="hidden">
          <CodeListTabPanel />
        </Box>
      );
    case 'skills-list':
      return (
        <Box h="full" w="full" overflow="hidden">
          <SkillsContent />
        </Box>
      );
    case 'skill':
      return <SkillDetailContent tab={tab} />;
    case 'skill-editor':
      return <SkillEditorContent tab={tab} />;
    case 'hitl-edit':
      return <HitlEditTab tab={tab} />;
    case 'http-viewer':
      return <HttpViewerTab tab={tab} />;
    case 'node-args-viewer':
      return <NodeArgsViewerTab tab={tab} />;
    case 'browser-stream':
      return <BrowserStreamTab tab={tab} />;
    case 'plan-viewer':
      return <PlanViewerTab tab={tab} />;
    case 'agent-executions-list':
      return (
        <Box h="full" w="full" overflow="hidden">
          <AgentExecutionsContent />
        </Box>
      );
    case 'co-pilot-execution':
      return <CoPilotExecutionTraceTab tab={tab} />;
    default:
      return <WelcomeTab />;
  }
}
